// State-Specific Statistics Provider Component
// Dynamically renders statistics based on month state

import React from 'react';
import { 
  Users, 
  Clock, 
  Euro, 
  DollarSign, 
  TrendingUp, 
  Calendar, 
  Activity, 
  CheckCircle, 
  CreditCard, 
  Archive,
  Shield,
  Target,
  BarChart,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { UserMonthState, getUserFriendlyStateConfig } from '@/lib/payroll/month-status-utils';
import { ConsolidatedPayrollStats } from './PayrollPeriodSelector';
import {
  getStateStatisticsConfig,
  StateSpecificStatistic
} from '@/lib/payroll/state-specific-statistics';

interface StateSpecificStatisticsProviderProps {
  userState: UserMonthState;
  consolidatedStats: ConsolidatedPayrollStats;
  countryCode?: string;
  className?: string;
}

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  Users,
  Clock,
  Euro,
  DollarSign,
  TrendingUp,
  Calendar,
  Activity,
  CheckCircle,
  CreditCard,
  Archive,
  Shield,
  Target,
  BarChart,
  AlertTriangle,
  HelpCircle
};

// Format currency based on country code
const formatCurrency = (amount: number, countryCode?: string): string => {
  if (typeof amount !== 'number') return '€0.00';
  
  if (countryCode && ['SK', 'CZ', 'AT', 'DE', 'FR'].includes(countryCode)) {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
  
  return `$${amount.toFixed(0)}`;
};

// Get appropriate icon component
const getIcon = (iconName: string): React.ComponentType<{className?: string}> => {
  return ICON_MAP[iconName as keyof typeof ICON_MAP] || HelpCircle;
};

// Render individual statistic item
const StatisticItem: React.FC<{
  statistic: StateSpecificStatistic;
  stats: ConsolidatedPayrollStats;
  countryCode?: string;
}> = ({ statistic, stats, countryCode }) => {
  const IconComponent = getIcon(statistic.iconName);
  let value = statistic.getValue(stats);
  
  // Format currency values
  if (typeof value === 'number' && statistic.key.includes('cost') || statistic.key.includes('pay')) {
    value = formatCurrency(value, countryCode);
  }

  const subtext = statistic.getSubtext(stats);
  const alert = statistic.getAlert?.(stats);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-1">
        <IconComponent className={`h-3 w-3 ${statistic.colorClass}`} />
        <span className="text-muted-foreground text-sm">{statistic.label}</span>
      </div>
      
      <div className="space-y-1">
        <p className="font-bold text-lg leading-none">
          {typeof value === 'string' ? value : value.toString()}
        </p>
        
        {subtext && (
          <p className="text-xs text-muted-foreground leading-none">
            {subtext}
          </p>
        )}
        
        {alert && (
          <Badge variant="outline" className="text-xs py-0 px-2">
            {alert}
          </Badge>
        )}
      </div>
    </div>
  );
};

// Main component
export const StateSpecificStatisticsProvider: React.FC<StateSpecificStatisticsProviderProps> = ({
  userState,
  consolidatedStats,
  countryCode,
  className = ''
}) => {
  const stateConfig = getUserFriendlyStateConfig(userState);
  const statisticsConfig = getStateStatisticsConfig(userState);

  // Get metrics to display (combine primary and secondary)
  const metricsToDisplay = [
    ...statisticsConfig.primaryMetrics,
    ...statisticsConfig.secondaryMetrics
  ].sort((a, b) => a.priority - b.priority);

  // No data scenario
  if (!consolidatedStats || consolidatedStats.totalEmployees === 0) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="space-y-3">
          <h4 className={`font-medium text-sm text-muted-foreground uppercase tracking-wider`}>
            {statisticsConfig.sectionHeaders[0] || stateConfig.label.toUpperCase()}
          </h4>
          
          <div className="text-center py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="space-y-2">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${stateConfig.secondaryColor} mb-2`}>
                {React.createElement(getIcon(stateConfig.iconName), { 
                  className: `h-5 w-5 ${stateConfig.primaryColor}` 
                })}
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                No data available for {stateConfig.label.toLowerCase()}
              </p>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                {getNoDataMessage(userState)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Section Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
            {statisticsConfig.sectionHeaders[0] || stateConfig.label.toUpperCase()}
          </h4>
          <Badge variant="outline" className={`text-xs ${stateConfig.secondaryColor}`}>
            {stateConfig.label}
          </Badge>
        </div>
        
        {/* Dynamic Grid Layout based on number of metrics */}
        <div className={`grid gap-6 text-sm ${getGridClasses(metricsToDisplay.length)}`}>
          {metricsToDisplay.map((metric) => (
            <StatisticItem
              key={metric.key}
              statistic={metric}
              stats={consolidatedStats}
              countryCode={countryCode}
            />
          ))}
        </div>
      </div>

    </div>
  );
};

// Helper functions

function getGridClasses(metricCount: number): string {
  if (metricCount <= 2) return 'grid-cols-1 md:grid-cols-2';
  if (metricCount <= 3) return 'grid-cols-1 md:grid-cols-3';
  if (metricCount <= 4) return 'grid-cols-2 lg:grid-cols-4';
  return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
}

function getNoDataMessage(userState: UserMonthState): string {
  switch (userState) {
    case 'FUTURE':
      return 'Create shift templates and schedules to see planning projections';
    case 'UPCOMING':
      return 'Schedule shifts for next month to see preparation status';
    case 'IN_PROGRESS':
      return 'Employees need to log time to see progress tracking';
    case 'TO_BE_PROCESSED':
      return 'Complete payroll calculations to see processing status';
    case 'PROCESSED':
      return 'No historical payroll data available for this period';
    default:
      return 'Complete the relevant actions to see statistics here';
  }
}

// Export individual pieces for testing
export {
  StatisticItem,
  getGridClasses,
  getIcon,
  formatCurrency
};